/**
 * Feature 2: Product Conversion Rate Leaderboard
 * Shows products with highest conversion rates
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import apiService from '../services/apiService';

const TopConvertingProducts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    fetchData();
  }, [limit]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getTopProducts(limit);
      setData(response.products);
    } catch (err) {
      setError('Failed to load product conversion data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const getConversionColor = (rate) => {
    if (rate >= 20) return 'success';
    if (rate >= 10) return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Card>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: '16px' }}>
            Loading top converting products...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <Typography variant="h5" gutterBottom>
              Top Converting Products
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Products with highest purchase/view conversion rates
            </Typography>
          </div>
          <TextField
            select
            label="Show"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            size="small"
            style={{ minWidth: '100px' }}
          >
            <MenuItem value={10}>Top 10</MenuItem>
            <MenuItem value={20}>Top 20</MenuItem>
            <MenuItem value={50}>Top 50</MenuItem>
            <MenuItem value={100}>Top 100</MenuItem>
          </TextField>
        </div>

        {data && data.length > 0 ? (
          <>
            {/* Summary Stats */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '150px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Average Conversion Rate
                </Typography>
                <Typography variant="h6">
                  {(data.reduce((sum, p) => sum + p.conversion_rate, 0) / data.length).toFixed(2)}%
                </Typography>
              </div>
              <div style={{ flex: '1', minWidth: '150px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Products
                </Typography>
                <Typography variant="h6">
                  {data.length}
                </Typography>
              </div>
              <div style={{ flex: '1', minWidth: '150px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Best Performer
                </Typography>
                <Typography variant="h6">
                  {data[0]?.conversion_rate.toFixed(2)}%
                </Typography>
              </div>
            </div>

            {/* Products Table */}
            <TableContainer component={Paper} style={{ maxHeight: '600px' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>Rank</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Product ID</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Brand</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Views</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Carts</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Purchases</TableCell>
                    <TableCell align="center" style={{ fontWeight: 'bold' }}>Conversion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((product, index) => (
                    <TableRow key={product.product_id} hover>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {index + 1}
                          {index < 3 && <TrendingUp style={{ color: '#4CAF50', fontSize: '16px' }} />}
                        </div>
                      </TableCell>
                      <TableCell>{product.product_id}</TableCell>
                      <TableCell>{product.brand}</TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
                          {product.category}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatPrice(product.price)}</TableCell>
                      <TableCell align="right">{formatNumber(product.views)}</TableCell>
                      <TableCell align="right">{formatNumber(product.carts)}</TableCell>
                      <TableCell align="right">{formatNumber(product.purchases)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${product.conversion_rate.toFixed(2)}%`}
                          color={getConversionColor(product.conversion_rate)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Alert severity="info">No products found</Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default TopConvertingProducts;
